/**
 * Оверлей «Объём 3D» (фаза S): полноэкранная сцена здания, построенная из
 * НАШЕГО нарисованного контура. three.js грузится ЛЕНИВО — только когда
 * пользователь открыл этот вид, и только если устройство его тянет
 * (capability.ts). Панель: этажи, разлёт, поворот/зум, список арендаторов.
 */
import { getLang, tr } from '@app/i18n'
import type { Venue } from '@app/rvf'
import { useCallback, useEffect, useRef, useState } from 'preact/hooks'
import { importChunk } from '../lazy-retry'
import { langTick, venue, volume3dUnitId } from '../state'
import type { TenantModel, VolumeModel } from '../volume3d/model'
import type { VolumeScene } from '../volume3d/scene'
import { useIndoorRoute } from './use-indoor-route'
import { Volume3dFinder } from './volume3d-finder'
import { Volume3dTenantCard } from './volume3d-tenant'

function close(): void {
  volume3dUnitId.value = null
}

/**
 * Ленивая загрузка three и создание сцены. Вынесено из компонента: там
 * зашкаливала сложность, а логика самостоятельная — «собрать модель, поднять
 * сцену, прибрать за собой».
 */
function useVolumeScene(
  unitId: string | null,
  v: Venue | null,
  hostRef: { current: HTMLDivElement | null },
  sceneRef: { current: VolumeScene | null },
  cb: {
    onModel: (m: VolumeModel | null) => void
    onTenant: (id: string) => void
    /** Тап по этажу в сцене: выбираем его и открываем интерьер. */
    onFloor: (floor: number | null) => void
    onDowngrade: () => void
    onFailed: (failed: boolean) => void
    onReset: () => void
  },
): void {
  useEffect(() => {
    if (!unitId || !v || !hostRef.current) return
    let disposed = false
    cb.onFailed(false)
    void (async () => {
      try {
        const [{ buildVolumeModel }, { createVolumeScene }] = await Promise.all([
          importChunk(() => import('../volume3d/model')),
          importChunk(() => import('../volume3d/scene')),
        ])
        const built = buildVolumeModel(v, unitId, getLang())
        const host = hostRef.current
        if (!built || !host || disposed) return
        cb.onModel(built)
        sceneRef.current = createVolumeScene(host, built, {
          onSelectFloor: cb.onFloor,
          onSelectTenant: cb.onTenant,
          onDowngrade: cb.onDowngrade,
        })
      } catch {
        // Не удалось (нет памяти/WebGL упал) — честно говорим и не ломаем вьюер.
        if (!disposed) cb.onFailed(true)
      }
    })()
    return () => {
      disposed = true
      sceneRef.current?.dispose()
      sceneRef.current = null
      cb.onReset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitId, v])
}

/** В счётчике этажа — только арендаторы: лифты и туалеты магазинами не считаем. */
function tenantCount(floor: { tenants: readonly TenantModel[] }): number {
  return floor.tenants.filter((t) => t.amenity !== true).length
}

/** Колонка этажей справа: тап открывает этаж изнутри (S8). */
function FloorChips({
  floors,
  active,
  onPick,
}: {
  floors: readonly { index: number; number: number; tenants: readonly TenantModel[] }[]
  active: number | null
  onPick: (floor: number | null) => void
}) {
  return (
    <aside class="v3d-side">
      <div class="v3d-floors">
        {floors
          .slice()
          .reverse()
          .map((f) => (
            <button
              key={f.index}
              type="button"
              class={`v3d-chip${active === f.index ? ' v3d-chip-on' : ''}`}
              onClick={() => onPick(active === f.index ? null : f.index)}
            >
              {tr('volume3d.floor_n', { n: String(f.number) })}
              {tenantCount(f) > 0 && <span class="v3d-count">{tenantCount(f)}</span>}
            </button>
          ))}
      </div>
    </aside>
  )
}

/**
 * Сколько подписей рисуем максимум. Каждая подпись — DOM-узел, который двигается
 * каждый кадр; на палубе с 44 каютами полсотни таких узлов и тормозят слабый
 * телефон, и превращают план в кашу. Ближние к центру экрана — важнее.
 */
const MAX_LABELS = 22

type Label = { id: string; x: number; y: number; visible: boolean }

/** Примерный размер подписи на экране, px — по нему разводим наложения. */
const LABEL_W = 76
const LABEL_H = 26

/**
 * Видимые подписи без наложений: идём от ближних к центру экрана и берём те,
 * что не перекрывают уже взятые. Дальние каюты проецируются вплотную друг к
 * другу, и без этого номера сливаются в нечитаемую кучу (фидбек 28.07).
 */
function pickLabels(labels: readonly Label[]): Label[] {
  const cx = window.innerWidth / 2
  const cy = window.innerHeight / 2
  const sorted = labels
    .filter((l) => l.visible)
    .sort((a, b) => (a.x - cx) ** 2 + (a.y - cy) ** 2 - ((b.x - cx) ** 2 + (b.y - cy) ** 2))
  const out: Label[] = []
  for (const lbl of sorted) {
    if (out.length >= MAX_LABELS) break
    const clash = out.some(
      (o) => Math.abs(o.x - lbl.x) < LABEL_W && Math.abs(o.y - lbl.y) < LABEL_H,
    )
    if (!clash) out.push(lbl)
  }
  return out
}

/** Подписи зон поверх канваса (интерьер): дешевле спрайтов и всегда читаемы. */
function ZoneLabels({
  labels,
  tenants,
  onPick,
}: {
  labels: readonly Label[]
  tenants: readonly { id: string; name: string; short?: string }[]
  onPick: (id: string) => void
}) {
  const byId = new Map(tenants.map((t) => [t.id, t]))
  return (
    <>
      {pickLabels(labels).map((lbl) => {
        const t = byId.get(lbl.id)
        if (!t) return null
        return (
          <button
            key={lbl.id}
            type="button"
            class="v3d-zone-label"
            style={{ left: `${lbl.x}px`, top: `${lbl.y}px` }}
            onClick={() => onPick(lbl.id)}
            title={t.name}
          >
            {t.short ?? t.name}
          </button>
        )
      })}
    </>
  )
}

/** Синхронизация сцены с состоянием панели + подписи зон в интерьере. */
function useSceneSync(
  sceneRef: { current: VolumeScene | null },
  state: { floor: number | null; explode: boolean; interior: boolean },
  onLabels: (l: { id: string; x: number; y: number; visible: boolean }[]) => void,
  ensureFloor: () => void,
): void {
  const { floor, explode, interior } = state

  useEffect(() => {
    sceneRef.current?.setActiveFloor(floor)
  }, [floor, sceneRef])

  useEffect(() => {
    sceneRef.current?.setExplode(explode)
  }, [explode, sceneRef])

  // Интерьер показывает выбранный этаж изнутри; без выбранного этажа он не
  // имеет смысла — включаем первый.
  useEffect(() => {
    if (interior) ensureFloor()
    sceneRef.current?.setInterior(interior)
  }, [interior, sceneRef, ensureFloor])

  // Подписи зон — обычный DOM поверх канваса: дешевле спрайтов и всегда читаемы.
  useEffect(() => {
    if (!interior) {
      onLabels([])
      return
    }
    let raf = 0
    const tick = (): void => {
      onLabels(sceneRef.current?.labelPositions() ?? [])
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [interior, sceneRef, onLabels])
}

/** Нижняя панель: интерьер/разлёт, поворот, зум, сброс вида. */
function ToolBar({
  interior,
  explode,
  onInterior,
  onExplode,
  scene,
}: {
  interior: boolean
  explode: boolean
  onInterior: () => void
  onExplode: () => void
  scene: { current: VolumeScene | null }
}) {
  return (
    <div class="v3d-tools">
      <button type="button" class={`v3d-btn${interior ? ' v3d-btn-on' : ''}`} onClick={onInterior}>
        {interior ? tr('volume3d.outside') : tr('volume3d.interior')}
      </button>
      {!interior && (
        <button type="button" class={`v3d-btn${explode ? ' v3d-btn-on' : ''}`} onClick={onExplode}>
          {tr('volume3d.explode')}
        </button>
      )}
      <button type="button" class="v3d-btn" onClick={() => scene.current?.rotateBy(-30)}>
        ⟲
      </button>
      <button type="button" class="v3d-btn" onClick={() => scene.current?.rotateBy(30)}>
        ⟳
      </button>
      <button type="button" class="v3d-btn" onClick={() => scene.current?.zoomBy(0.85)}>
        ＋
      </button>
      <button type="button" class="v3d-btn" onClick={() => scene.current?.zoomBy(1.18)}>
        －
      </button>
      <button type="button" class="v3d-btn" onClick={() => scene.current?.resetView()}>
        {tr('volume3d.reset')}
      </button>
    </div>
  )
}

/** Состояние оверлея: сцена, выбранный этаж, режимы, карточка арендатора. */
function useOverlayState(unitId: string | null, v: Venue | null) {
  const hostRef = useRef<HTMLDivElement | null>(null)
  const sceneRef = useRef<VolumeScene | null>(null)
  const [model, setModel] = useState<VolumeModel | null>(null)
  const [floor, setFloor] = useState<number | null>(null)
  const [explode, setExplode] = useState(false)
  const [downgraded, setDowngraded] = useState(false)
  const [failed, setFailed] = useState(false)
  const [interior, setInterior] = useState(false)
  const [tenantId, setTenantId] = useState<string | null>(null)
  const [labels, setLabels] = useState<{ id: string; x: number; y: number; visible: boolean }[]>([])
  const floorRef = useRef<number | null>(null)
  floorRef.current = floor

  useVolumeScene(unitId, v, hostRef, sceneRef, {
    onModel: setModel,
    onTenant: (id) => setTenantId(id),
    // Тап по этажу снаружи открывает его изнутри — так же, как чип этажа
    // (запрос владельца 28.07: «нажать на этаж и попасть в „Интерьер“»).
    onFloor: (next) => {
      if (next === null) return
      setFloor(next)
      setInterior(true)
      setTenantId(null)
    },
    onDowngrade: () => setDowngraded(true),
    onFailed: setFailed,
    onReset: () => {
      setModel(null)
      setFloor(null)
      setExplode(false)
      setInterior(false)
      setTenantId(null)
    },
  })

  useSceneSync(
    sceneRef,
    { floor, explode, interior },
    setLabels,
    useCallback(() => {
      if (floorRef.current === null) setFloor(1)
    }, []),
  )

  // Esc закрывает — привычно для полноэкранного вида.
  useEffect(() => {
    if (!unitId) return
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') close()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [unitId])

  return {
    hostRef,
    sceneRef,
    model,
    floor,
    setFloor,
    explode,
    setExplode,
    interior,
    setInterior,
    tenantId,
    setTenantId,
    labels,
    downgraded,
    failed,
  }
}

/** Карточка выбранного арендатора: данные + «Маршрут»/«QR» (S8). */
function SelectedTenant({
  tenantId,
  tenants,
  zones,
  floor,
  onClose,
  onRouteInside,
  canRouteInside,
}: {
  tenantId: string | null
  tenants: readonly TenantModel[]
  zones: readonly { tenantId: string; areaM2: number }[]
  floor: number
  onClose: () => void
  onRouteInside: () => void
  canRouteInside: boolean
}) {
  if (!tenantId) return null
  const tenant = tenants.find((t) => t.id === tenantId)
  if (!tenant) return null
  const areaM2 = zones.find((z) => z.tenantId === tenantId)?.areaM2 ?? null
  return (
    <Volume3dTenantCard
      tenant={tenant}
      areaM2={areaM2}
      floor={floor}
      onClose={onClose}
      canRouteInside={canRouteInside}
      onRouteInside={onRouteInside}
    />
  )
}

export function Volume3dOverlay() {
  langTick.value // подписка на смену языка
  const unitId = volume3dUnitId.value
  const v = venue.value
  const {
    hostRef,
    sceneRef,
    model,
    floor,
    setFloor,
    explode,
    setExplode,
    interior,
    setInterior,
    tenantId,
    setTenantId,
    labels,
    downgraded,
    failed,
  } = useOverlayState(unitId, v)

  const routeState = useIndoorRoute(model, floor, tenantId, sceneRef)
  const [finderOpen, setFinderOpen] = useState(false)

  // Ctrl/⌘+K — привычное сочетание для поиска (десктоп).
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setFinderOpen((v) => !v)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  if (!unitId) return null
  const floors = model?.floors ?? []
  const activeTenants = floor !== null ? (floors[floor - 1]?.tenants ?? []) : []

  return (
    <div class="v3d-overlay" role="dialog" aria-label={tr('volume3d.title')}>
      <div class="v3d-canvas" ref={hostRef} />

      <header class="v3d-top">
        <button type="button" class="v3d-btn" onClick={close}>
          ← {tr('common.back')}
        </button>
        <span class="v3d-title">{model?.title ?? ''}</span>
      </header>

      {failed && <p class="v3d-note">{tr('volume3d.failed')}</p>}
      {downgraded && !failed && <p class="v3d-note">{tr('volume3d.downgraded')}</p>}

      <FloorChips
        floors={floors}
        active={floor}
        onPick={(next) => {
          setFloor(next)
          setInterior(next !== null)
          setTenantId(null)
        }}
      />

      <ToolBar
        interior={interior}
        explode={explode}
        onInterior={() => setInterior(!interior)}
        onExplode={() => setExplode(!explode)}
        scene={sceneRef}
      />

      {interior && (
        <ZoneLabels labels={labels} tenants={activeTenants} onPick={(id) => setTenantId(id)} />
      )}

      <SelectedTenant
        tenantId={tenantId}
        tenants={activeTenants}
        zones={floor !== null ? (floors[floor - 1]?.zones ?? []) : []}
        floor={floor ?? 1}
        onClose={() => setTenantId(null)}
        canRouteInside={interior && routeState.can}
        onRouteInside={() => {
          routeState.run()
          // Карточка закрывает середину сцены — убираем её, чтобы был виден путь.
          setTenantId(null)
        }}
      />
      {routeState.hint && <p class="v3d-route-hint">{routeState.hint}</p>}

      <button
        type="button"
        class="v3d-search-btn"
        onClick={() => setFinderOpen(true)}
        aria-label={tr('volume3d.search')}
      >
        ⌕ <span class="v3d-search-btn-text">{tr('volume3d.search_short')}</span>
      </button>

      <Volume3dFinder
        model={model}
        open={finderOpen}
        onClose={() => setFinderOpen(false)}
        onPick={(hit) => {
          // Нашли — показываем этаж изнутри и открываем карточку объекта.
          setFloor(hit.floor)
          setInterior(true)
          setTenantId(hit.id)
          setFinderOpen(false)
        }}
      />
    </div>
  )
}
